import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import TopBannerGuidance from '../../../components/TopBannerGuidance';
import OwlMascot from '../../../assets/貓頭鷹博士_雙手.gif';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';
import url from '../../../url.json';
import {
  Book as BookIcon,
  Person as PersonIcon,
  School as ClassIcon,
  Settings as PrepSettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Home as PrepHomeIcon,
  Send as SendIcon
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';

export default function StudentManagement() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showOwlGuidance, setShowOwlGuidance] = useState(true);

  // 載入老師的學生清單
  const fetchTeacherStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('正在載入老師的學生清單...');
      const response = await axiosInstance.get(`${url.backendHost}${config.getTeacherStudents}`);
      
      console.log('學生清單載入成功:', response.data);
      
      if (response.data.success) {
        setStudents(response.data.data || []);
      } else {
        setError(response.data.message || '載入學生清單失敗');
      }
    } catch (error) {
      console.error('載入學生清單失敗:', error);
      setError(error.response?.data?.message || '載入學生清單失敗');
    } finally {
      setLoading(false);
    }
  };

  // 組件載入時獲取學生清單
  useEffect(() => {
    fetchTeacherStudents();
  }, []);

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'students', navigate);

  // 處理學生資料格式，確保與前端顯示相容
  const processedStudents = students.map(student => ({
    id: student.id,
    name: student.student_name,
    studentId: student.student_number || '未設定',
    email: student.student_email || '未設定',
    class: student.class_name || '未設定',
    joinDate: new Date(student.created_at).toLocaleDateString('zh-TW'),
    avatar: student.student_name ? student.student_name.charAt(0) : '?',
    student_id: student.student_id, // 用於判斷是否已註冊
    autoRegistered: student.student_id !== null
  }));

  // 只使用搜尋過濾
  const filteredStudents = processedStudents.filter(student => {
    return student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
           student.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="teacher-layout student-management-container">
      <div className="main-content-v2">
        <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">學生管理</h1>
          {/* 篩選區域在標題下方置中 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋學生"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => setSearchTerm('')}
          >
            清除搜尋
          </button>
          </div>
        </div>

        <Box sx={{ p: 2 }}>
          {/* 操作按鈕區域 */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <button
              className="btn-primary"
              onClick={() => setOpenCreateDialog(true)}
              disabled={loading}
            >
              新增學生
            </button>
            <button
              className="btn-secondary"
              onClick={() => setOpenUploadDialog(true)}
              disabled={loading}
            >
              批次匯入
            </button>
          </Box>

          {/* 載入狀態 */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>載入中...</Typography>
            </Box>
          )}

          {/* 錯誤狀態 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              <Button onClick={fetchTeacherStudents} sx={{ ml: 2 }}>
                重新載入
              </Button>
            </Alert>
          )}

          {/* 頂部橫幅提醒 */}
          <TopBannerGuidance
            show={showOwlGuidance}
            onClose={() => setShowOwlGuidance(false)}
            severity={students.length >= 5 ? 'success' : 'info'}
            mascotImage={OwlMascot}
            message={
              students.length === 0
                ? '先建立學生名單，可以使用「批次匯入」功能快速匯入，或逐筆新增學生資料。建立完成後，就可以在社群管理中將學生分配到班級了。'
                : students.length < 5
                ? `目前有 ${students.length} 位學生。建議至少建立 5 位以上學生，方便進行分組活動。可以使用「批次匯入」功能快速新增更多學生。`
                : `很好！目前有 ${students.length} 位學生。接下來可以前往「社群管理」建立班級，並將學生分配到班級中。`
            }
          />

          {/* 學生清單表格 */}
          {!loading && !error && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>學生</TableCell>
                    <TableCell>學號</TableCell>
                    <TableCell>班級</TableCell>
                    {/* <TableCell>狀態</TableCell> */} {/* 移除狀態欄位 */}
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          沒有找到學生資料
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: '#2196f3' }}>
                          {student.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{student.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                        <TableCell>{student.studentId}</TableCell>
                    <TableCell>{student.class}</TableCell>
                        {/* <TableCell>
                          <Chip 
                            label={student.status} 
                            color={student.status === '在學' ? 'success' : 'default'}
                            size="small"
                          />
                          {student.autoRegistered && (
                            <Chip 
                              label="已註冊" 
                              color="primary"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell> */} {/* 移除狀態和註冊顯示 */}
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          setSelectedStudent(student);
                          setOpenViewDialog(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          setSelectedStudent(student);
                          setOpenEditDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedStudent(student);
                          setOpenDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Box>

        {/* 新增學生對話框 */}
        <CreateStudentDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenCreateDialog(false);
            fetchTeacherStudents(); // 重新載入學生清單
          }}
        />

        {/* 批次匯入對話框 */}
        <UploadStudentsDialog
          open={openUploadDialog}
          onClose={() => setOpenUploadDialog(false)}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenUploadDialog(false);
            fetchTeacherStudents(); // 重新載入學生清單
          }}
        />

        {/* 查看學生對話框 */}
        <ViewStudentDialog
          open={openViewDialog}
          onClose={() => {
            setOpenViewDialog(false);
            setSelectedStudent(null);
          }}
          student={selectedStudent}
        />

        {/* 修改學生對話框 */}
        <EditStudentDialog
          open={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedStudent(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenEditDialog(false);
            setSelectedStudent(null);
            fetchTeacherStudents(); // 重新載入學生清單
          }}
          student={selectedStudent}
        />

        {/* 刪除確認對話框 */}
        <DeleteStudentDialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setSelectedStudent(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenDeleteDialog(false);
            setSelectedStudent(null);
            fetchTeacherStudents(); // 重新載入學生清單
          }}
          student={selectedStudent}
        />

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </div>
    </div>
  );
}

// 新增學生對話框組件
function CreateStudentDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    student_name: '',
    student_number: '',
    student_email: '',
    class_name: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  const schools = ['中央大學', '台灣大學', '清華大學', '交通大學', '成功大學'];
  const cities = ['桃園市', '台北市', '新北市', '台中市', '台南市', '高雄市'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 簡化：僅需要帳號（若已有帳號即可加入；沒有帳號時再補資料）
    if (!formData.student_email || formData.student_email.trim() === '') {
      alert('帳號為必填欄位');
      return;
    }

    setLoading(true);
    try {
      console.log('正在創建學生:', formData);
      
      // 第一步僅提交最小必要資訊：email 與可選的學號
      const requestData = {
        student_email: formData.student_email,
        student_number: formData.student_number || undefined
      };

      const response = await axiosInstance.post(
        `${url.backendHost}${config.addStudentToTeacher}`,
        requestData
      );

      console.log('學生創建成功:', response.data);
      
      if (response.data.success) {
        onSuccess(response.data.message);
        
        // 重置表單
        setFormData({
          student_name: '',
          student_number: '',
          student_email: '',
          class_name: '',
          password: ''
        });
      } else {
        // 如果返回 success: false，檢查是否有 needsPassword 標記
        if (response.data.needsPassword) {
          // 需要補註冊資料：顯示完整註冊對話框（姓名 + 密碼 + 其他）
          setShowPasswordDialog(true);
          setLoading(false); // 不要設置 loading 為 false，因為會由 finally 處理
          return; // 直接返回，不顯示錯誤
        } else {
          throw new Error(response.data.message || '創建失敗');
        }
      }
    } catch (error) {
      console.error('創建學生失敗:', error);
      // 檢查錯誤響應中是否有 needsPassword 標記（HTTP 400 錯誤）
      if (error.response?.status === 400 && error.response?.data?.needsPassword) {
        // 需要補註冊資料：顯示完整註冊對話框（姓名 + 密碼 + 其他）
        setShowPasswordDialog(true);
      } else {
        alert('創建失敗：' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // 處理密碼提交（補註冊：無帳號時）
  const handlePasswordSubmit = async () => {
    if (!formData.student_name) { alert('請輸入學生姓名'); return; }
    if (!passwordInput) { alert('請輸入密碼'); return; }

    setLoading(true);
    try {
      const requestData = {
        student_name: formData.student_name,
        student_number: formData.student_number || undefined,
        student_email: formData.student_email,
        class_name: formData.class_name || undefined,
        password: passwordInput
      };

      const response = await axiosInstance.post(
        `${url.backendHost}${config.addStudentToTeacher}`,
        requestData
      );
      
      if (response.data.success) {
        onSuccess(response.data.message);
        // 重置表單
        setFormData({ student_name: '', student_number: '', student_email: '', class_name: '', password: '' });
        setPasswordInput('');
        setShowPasswordDialog(false);
      } else {
        throw new Error(response.data.message || '註冊失敗');
      }
    } catch (error) {
      alert('註冊失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      sx={{
        zIndex: 2000,
        '& .MuiDialog-paper': {
          zIndex: 2000
        }
      }}
      PaperProps={{
        sx: {
          zIndex: 2000
        }
      }}
    >
      <DialogTitle>新增學生</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="學號"
                value={formData.student_number}
                onChange={(e) => setFormData({ ...formData, student_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="帳號"
                value={formData.student_email}
                onChange={(e) => setFormData({ ...formData, student_email: e.target.value })}
                required
                
              />
            </Grid>
            {/* 其餘資料待後續補註冊時再填寫 */}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} disabled={loading}>取消</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? '處理中...' : '加入/建立'}
          </Button>
        </DialogActions>
      </form>

      {/* 密碼輸入對話框 */}
      <Dialog 
        open={showPasswordDialog} 
        onClose={() => setShowPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
      >
      <DialogTitle>完成註冊（新帳號）</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          該學生尚未註冊，請補齊基本資料以完成註冊：
          </Typography>
        <TextField
          fullWidth
          label="學生姓名"
          value={formData.student_name}
          onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
          sx={{ mb: 2 }}
          required
        />
          <TextField
            fullWidth
            label="密碼"
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            placeholder="請輸入密碼"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handlePasswordSubmit();
              }
            }}
          />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="班級（選填）"
            value={formData.class_name}
            onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
          />
        </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setShowPasswordDialog(false)} disabled={loading}>取消</Button>
          <Button onClick={handlePasswordSubmit} variant="contained" disabled={loading}>
            {loading ? '註冊中...' : '確認註冊'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// 批次匯入對話框組件
function UploadStudentsDialog({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // 預覽 CSV 內容
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('請選擇檔案');
      return;
    }

    setLoading(true);
    try {
      // 讀取 CSV 檔案內容，指定 UTF-8 編碼
      const text = await file.text();
      console.log('原始檔案內容:', text);
      
      // 處理不同的換行符號
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      console.log('解析後的行數:', lines.length);
      
      if (lines.length < 2) {
        throw new Error('檔案格式錯誤，至少需要標題行和一行資料');
      }

      // 解析 CSV 資料，處理引號和逗號
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const headers = parseCSVLine(lines[0]);
      console.log('標題行:', headers);
      const students = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        console.log(`第 ${i} 行資料:`, values);
        
        if (values.length >= 4) { // 至少需要姓名、電子郵件、密碼、學號
          // 檢查是否為空行或無效資料
          if (values[0] && values[1] && values[2]) {
            students.push({
              student_name: values[0],
              student_email: values[1],
              password: values[2],
              student_number: values[3] || '',
              class_name: values[4] || '未設定',
              school: values[5] || '未設定'
            });
          }
        }
      }

      console.log('解析後的學生資料:', students);

      if (students.length === 0) {
        throw new Error('沒有找到有效的學生資料');
      }

      // 調用批次匯入 API
      const response = await axiosInstance.post(config.batchImportStudents, {
        students: students,
        defaultPassword: null // 不提供預設密碼，讓系統判斷
      });

      if (response.data.success) {
        onSuccess(response.data.message);
        setFile(null);
        setPreview(null);
      } else {
        throw new Error(response.data.message || '匯入失敗');
      }
    } catch (error) {
      console.error('批次匯入錯誤:', error);
      alert('匯入失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // 使用 BOM (Byte Order Mark) 確保 Excel 正確識別 UTF-8 編碼
    const BOM = '\uFEFF';
    const csvContent = BOM + "學生姓名,電子郵件,密碼,學號,班級,學校\n王小明,wang@example.com,password123,S001,一年一班,中央大學\n李小華,li@example.com,password123,S002,一年一班,中央大學";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '學生資料範本.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      sx={{
        zIndex: 2000,
        '& .MuiDialog-paper': {
          zIndex: 2000
        }
      }}
      PaperProps={{
        sx: {
          zIndex: 2000
        }
      }}
    >
      <DialogTitle>批次匯入學生</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>匯入說明</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            1. 請下載範本檔案，按照格式填寫學生資料
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            2. 支援 CSV 格式檔案，欄位順序：學生姓名,電子郵件,密碼,學號,班級,學校
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            3. 系統會自動為學生創建帳號並完成註冊
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            4. 如果電子郵件已存在，會自動關聯到現有帳號
          </Typography>
          
          <Button
            variant="contained"
            onClick={downloadTemplate}
            startIcon={<SendIcon />}
            className="btn-primary"
            sx={{ mt: 2 }}
          >
            下載範本
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<UploadIcon />}
              className="btn-primary"
            >
              選擇檔案
            </Button>
          </label>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              已選擇: {file.name}
            </Typography>
          )}
        </Box>

        {preview && (
          <Box>
            <Typography variant="h6" gutterBottom>檔案預覽</Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                border: '1px solid #ddd',
                p: 2,
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                fontSize: '0.8rem'
              }}
            >
              <pre>{preview}</pre>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} disabled={loading}>取消</Button>
        <Button 
          onClick={handleUpload} 
          variant="contained"
          disabled={!file || loading}
        >
          {loading ? '匯入中...' : '開始匯入'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 查看學生對話框組件
function ViewStudentDialog({ open, onClose, student }) {
  if (!student) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>查看學生資料</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">姓名</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{student.name || '未設定'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">帳號</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{student.email || '未設定'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">學號</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{student.studentId || '未設定'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">班級</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{student.class || '未設定'}</Typography>
            </Grid>
            <Grid item xs={12}>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">加入日期</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{student.joinDate || '未設定'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">帳號狀態</Typography>
              <Chip 
                label={student.autoRegistered ? '已註冊' : '未註冊'} 
                color={student.autoRegistered ? 'success' : 'default'}
                size="small"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>關閉</Button>
      </DialogActions>
    </Dialog>
  );
}

// 修改學生對話框組件
function EditStudentDialog({ open, onClose, onSuccess, student }) {
  const [formData, setFormData] = useState({
    student_name: '',
    student_email: '',
    student_number: '',
    class_name: ''
  });
  const [loading, setLoading] = useState(false);

  // 當 student 或 open 改變時，更新表單資料
  useEffect(() => {
    if (student && open) {
      console.log('[前端] 編輯學生對話框 - 載入學生資料:', student);
      setFormData({
        student_name: student.name || '',
        student_email: student.email || '',
        student_number: student.studentId !== '未設定' ? student.studentId : '',
        class_name: student.class !== '未設定' ? student.class : ''
      });
    }
  }, [student, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.student_name || !formData.student_email || formData.student_email.trim() === '') {
      alert('姓名和帳號為必填欄位');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.put(
        `${url.backendHost}${config.updateStudent}${student.id}`,
        formData
      );

      if (response.data.success) {
        onSuccess(response.data.message || '學生資料更新成功');
      } else {
        throw new Error(response.data.message || '更新失敗');
      }
    } catch (error) {
      console.error('更新學生資料失敗:', error);
      alert('更新失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>修改學生資料</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="學生姓名"
                value={formData.student_name}
                onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="帳號（電子郵件或使用者名稱）"
                value={formData.student_email}
                onChange={(e) => setFormData({ ...formData, student_email: e.target.value })}
                required
                helperText="可用電子郵件或任何使用者名稱作為帳號"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="學號"
                value={formData.student_number}
                onChange={(e) => setFormData({ ...formData, student_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="班級"
                value={formData.class_name}
                onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? '更新中...' : '確認更新'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// 刪除學生確認對話框組件
function DeleteStudentDialog({ open, onClose, onSuccess, student }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!student) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        `${url.backendHost}${config.deleteStudent}${student.id}`
      );

      if (response.data.success) {
        onSuccess(response.data.message || '學生已刪除');
      } else {
        throw new Error(response.data.message || '刪除失敗');
      }
    } catch (error) {
      console.error('刪除學生失敗:', error);
      alert('刪除失敗：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>確認刪除</DialogTitle>
      <DialogContent>
        <DialogContentText>
          確定要刪除學生 <strong>{student.name}</strong> ({student.email}) 嗎？
          <br />
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            此操作無法復原
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} disabled={loading}>取消</Button>
        <Button 
          onClick={handleDelete} 
          variant="contained"
          disabled={loading}
        >
          {loading ? '刪除中...' : '確認刪除'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
